import type { Product } from "./types";
import { inputClass } from "./constants";

export const RecommendationNotice = () => (
  <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
    <p className="text-sm text-gray-700">
      <span className="font-semibold text-[#5c2f2f]">Recommendations:</span>{" "}
      Create the product first, then click <span className="font-semibold">Edit</span>{" "}
      to add recommended products.
    </p>
    <p className="text-xs text-gray-500 mt-2">
      The first active size is used as the default product summary in the admin
      list.
    </p>
  </div>
);

type RecommendationsSectionProps = {
  recLoading: boolean;
  recSaving: boolean;
  recQuery: string;
  setRecQuery: (query: string) => void;
  filteredProductsForRec: Product[];
  recommendedIds: string[];
  idToProduct: Map<string, Product>;
  addRecommended: (id: string) => void;
  removeRecommended: (id: string) => void;
  moveRecommended: (id: string, dir: -1 | 1) => void;
  saveRecommendations: () => void;
};

export const RecommendationsSection = ({
  recLoading,
  recSaving,
  recQuery,
  setRecQuery,
  filteredProductsForRec,
  recommendedIds,
  idToProduct,
  addRecommended,
  removeRecommended,
  moveRecommended,
  saveRecommendations,
}: RecommendationsSectionProps) => (
  <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
    <h2 className="text-xl font-semibold text-[#5c2f2f] mb-4">
      Recommended Products
    </h2>

    {recLoading ? (
      <div className="text-sm text-gray-600">Loading recommendations...</div>
    ) : (
      <>
        <input
          type="text"
          placeholder="Search products to add"
          value={recQuery}
          onChange={(e) => setRecQuery(e.target.value)}
          className={`${inputClass} mb-4`}
        />

        <div className="max-h-60 overflow-auto border border-gray-200 rounded-2xl bg-gray-50">
          {filteredProductsForRec.length === 0 ? (
            <div className="p-4 text-sm text-gray-500">No matching products.</div>
          ) : (
            filteredProductsForRec.map((product) => {
              const already = recommendedIds.includes(product.id);
              return (
                <div
                  key={product.id}
                  className="flex items-center justify-between gap-3 px-4 py-3 border-b last:border-b-0 bg-white"
                >
                  <div className="flex items-center gap-3">
                    {product.image_url ? (
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-12 h-12 object-cover rounded-xl"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gray-200 rounded-xl" />
                    )}
                    <div>
                      <div className="text-sm font-medium text-gray-800">
                        {product.name}
                      </div>
                      <div className="text-xs text-gray-500">${product.price}</div>
                    </div>
                  </div>

                  <button
                    type="button"
                    disabled={already}
                    onClick={() => addRecommended(product.id)}
                    className={`px-4 py-2 rounded-xl text-sm ${
                      already
                        ? "bg-gray-200 text-gray-500"
                        : "bg-[#5c2f2f] text-white hover:bg-[#7a4444]"
                    }`}
                  >
                    + Add
                  </button>
                </div>
              );
            })
          )}
        </div>

        <div className="mt-5">
          <h3 className="font-semibold text-gray-800 mb-3">
            Selected Recommendations
          </h3>

          {recommendedIds.length === 0 ? (
            <div className="text-sm text-gray-500">No recommended products.</div>
          ) : (
            <div className="space-y-3">
              {recommendedIds.map((recommendationId) => {
                const product = idToProduct.get(recommendationId);
                if (!product) return null;

                return (
                  <div
                    key={recommendationId}
                    className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-2xl p-3"
                  >
                    <div className="flex items-center gap-3">
                      {product.image_url ? (
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-12 h-12 object-cover rounded-xl"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gray-200 rounded-xl" />
                      )}
                      <div className="text-sm font-medium text-gray-800">
                        {product.name}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => moveRecommended(recommendationId, -1)}
                        className="px-3 py-2 border border-gray-300 rounded-xl bg-white"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => moveRecommended(recommendationId, 1)}
                        className="px-3 py-2 border border-gray-300 rounded-xl bg-white"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        onClick={() => removeRecommended(recommendationId)}
                        className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            disabled={recSaving}
            onClick={saveRecommendations}
            className="px-5 py-3 rounded-xl bg-[#5c2f2f] text-white hover:bg-[#7a4444] disabled:opacity-50"
          >
            {recSaving ? "Saving..." : "Save Recommendations"}
          </button>
        </div>
      </>
    )}
  </div>
);
